
import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LocationAutocomplete } from '@/components/dashboard/LocationAutocomplete';
import { BreedSelector } from '@/components/BreedSelector';

interface FormData {
  title: string;
  description: string;
  price: string;
  price_currency: string;
  breed: string;
  type: string;
  age: string;
  weight: string;
  weight_unit: string;
  location: string;
  registration_number: string;
  listing_type: string;
  youtube_video_url: string;
}

interface BasicDetailsFormProps {
  formData: FormData;
  onInputChange: (field: string, value: string) => void;
  onLocationChange?: (location: string, coordinates?: [number, number]) => void;
}

const YOUTUBE_URL_REGEX = /^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|embed\/)|youtu\.be\/)[a-zA-Z0-9_-]+/;

const BasicDetailsForm: React.FC<BasicDetailsFormProps> = ({ 
  formData, 
  onInputChange, 
  onLocationChange 
}) => {
  const [youtubeError, setYoutubeError] = useState(false);

  const handleLocationChange = (location: string, coordinates?: [number, number]) => {
    onInputChange('location', location);
    if (onLocationChange) {
      onLocationChange(location, coordinates);
    }
  };

  const handleYoutubeChange = (value: string) => {
    onInputChange('youtube_video_url', value);
    if (value.trim() && !YOUTUBE_URL_REGEX.test(value.trim())) {
      setYoutubeError(true);
    } else {
      setYoutubeError(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm mb-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className="md:col-span-2">
          <Label htmlFor="title">Title *</Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => onInputChange('title', e.target.value)}
            placeholder="e.g., Premium Angus Bull - Bloodline Champion"
          />
        </div>
        <div>
          <Label htmlFor="price">Price (if not applicable leave empty)</Label>
          <div className="flex gap-2">
            <Input
              id="price"
              type="number"
              value={formData.price}
              onChange={(e) => onInputChange('price', e.target.value)}
              placeholder="15000"
              className="flex-1"
            />
            <Select value={formData.price_currency} onValueChange={(value) => onInputChange('price_currency', value)}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="GBP">GBP</SelectItem>
                <SelectItem value="CAD">CAD</SelectItem>
                <SelectItem value="AUD">AUD</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label htmlFor="breed">Breed *</Label>
          <BreedSelector
            value={formData.breed}
            onValueChange={(value) => onInputChange('breed', value)}
            placeholder="Select breed"
            className="w-full"
          />
        </div>
        <div>
          <Label htmlFor="type">Type *</Label>
          <Select value={formData.type} onValueChange={(value) => onInputChange('type', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Bull">Bull</SelectItem>
              <SelectItem value="Cow">Cow</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="listing_type">Listing Type (optional)</Label>
          <Select value={formData.listing_type} onValueChange={(value) => onInputChange('listing_type', value === 'none' ? '' : value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select listing type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="Semen Sale">Semen Sale</SelectItem>
              <SelectItem value="Live Animal">Live Animal</SelectItem>
              <SelectItem value="Auction">Auction</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="age">Age (in years)</Label>
          <Input
            id="age"
            type="number"
            value={formData.age}
            onChange={(e) => onInputChange('age', e.target.value)}
            placeholder="3"
          />
        </div>
        <div>
          <Label htmlFor="weight">Weight (if not applicable leave empty)</Label>
          <div className="flex gap-2">
            <Input
              id="weight"
              type="number"
              value={formData.weight}
              onChange={(e) => onInputChange('weight', e.target.value)}
              placeholder="2200"
              className="flex-1"
            />
            <Select value={formData.weight_unit} onValueChange={(value) => onInputChange('weight_unit', value)}>
              <SelectTrigger className="w-16">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lbs">lbs</SelectItem>
                <SelectItem value="kg">kg</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <LocationAutocomplete
            value={formData.location}
            onChange={handleLocationChange}
            placeholder="Search for a location..."
          />
        </div>
        <div>
          <Label htmlFor="registration_number">Registration Number (optional)</Label>
          <Input
            id="registration_number"
            value={formData.registration_number}
            onChange={(e) => onInputChange('registration_number', e.target.value)}
            placeholder="e.g., ABC123456789"
          />
        </div>
      </div>

      <div className="mb-6">
        <Label htmlFor="youtube_video_url">YouTube Video URL (optional)</Label>
        <Input
          id="youtube_video_url"
          value={formData.youtube_video_url}
          onChange={(e) => handleYoutubeChange(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
        />
        {youtubeError && (
          <p className="text-sm text-destructive mt-1">
            Please enter a valid YouTube URL (youtube.com/watch, youtu.be, or youtube.com/embed).
          </p>
        )}
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => onInputChange('description', e.target.value)}
          rows={4}
          placeholder="Describe the animal's characteristics, lineage, and any other relevant information..."
        />
      </div>
    </div>
  );
};

export default BasicDetailsForm;
